import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Car, Clock, Calendar, CheckCircle, XCircle, Accessibility, Zap, Clock3, Edit3, Trash2, Plus, User, CalendarOff, LogIn, LogOut } from 'lucide-react'
import { parkingSpotApi, parkingReservationApi } from '../services/api'
import { extractErrorMessage } from '../utils/errorUtils'
import { showError } from '../utils/showError'
import {
  DetailPageLayout,
  Card,
  CardHeader,
  CardBody,
  LoadingSpinner,
  ErrorMessage,
  Badge,
  Button,
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  EmptyState,
  FormField,
  FormTextarea,
  ConfirmationModal
} from '../components/common'

const ParkingSpotDetail = () => {
  const { parkingSpotId, buildingId } = useParams()
  const navigate = useNavigate()
  const [spot, setSpot] = useState(null)
  const [reservations, setReservations] = useState([])
  const [loading, setLoading] = useState(true)
  const [reservationsLoading, setReservationsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({ open: false, title: '', message: '', onConfirm: null, variant: 'danger' })

  // Unavailable date form
  const [showUdForm, setShowUdForm] = useState(false)
  const [udForm, setUdForm] = useState({ from: '', to: '', reason: '' })
  const [udSubmitting, setUdSubmitting] = useState(false)

  const fetchSpot = async () => {
    try {
      setLoading(true)
      const response = await parkingSpotApi.get(parkingSpotId)
      const data = response.data?.data || response.data
      setSpot(data)
    } catch (err) {
      setError(extractErrorMessage(err))
      console.error('Error fetching parking spot:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchReservations = async () => {
    try {
      setReservationsLoading(true)
      const response = await parkingReservationApi.getByParkingSpotId(parkingSpotId)
      const data = response.data?.data || response.data
      const items = Array.isArray(data) ? data : []
      setReservations(items)
    } catch (err) {
      console.error('Error fetching reservations:', err)
      setReservations([])
    } finally {
      setReservationsLoading(false)
    }
  }

  useEffect(() => {
    fetchSpot()
    fetchReservations()
  }, [parkingSpotId])

  const handleConfirm = async (reservationId) => {
    try {
      await parkingReservationApi.confirm(reservationId)
      fetchReservations()
    } catch (err) {
      console.error('Error confirming reservation:', err)
      showError(err)
    }
  }

  const handleDecline = async (reservationId) => {
    try {
      await parkingReservationApi.decline(reservationId)
      fetchReservations()
    } catch (err) {
      console.error('Error declining reservation:', err)
      showError(err)
    }
  }

  const handleDeleteReservation = (reservationId) => {
    setConfirmModal({
      open: true,
      title: 'Delete Reservation',
      message: 'Are you sure you want to delete this reservation? This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await parkingReservationApi.delete(reservationId)
          fetchReservations()
        } catch (err) {
          console.error('Error deleting reservation:', err)
          showError(err)
        }
        setConfirmModal(prev => ({ ...prev, open: false }))
      }
    })
  }

  const handleDeleteSpot = () => {
    setConfirmModal({
      open: true,
      title: 'Delete Parking Spot',
      message: 'Are you sure you want to delete this parking spot? All associated reservations will also be deleted. This action cannot be undone.',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await parkingSpotApi.delete(parkingSpotId)
          navigate('/parking')
        } catch (err) {
          console.error('Error deleting parking spot:', err)
          showError(err)
        }
        setConfirmModal(prev => ({ ...prev, open: false }))
      }
    })
  }

  const handleAddUnavailableDate = async () => {
    if (!udForm.from || !udForm.to) return
    try {
      setUdSubmitting(true)
      await parkingSpotApi.addUnavailableDate(parkingSpotId, {
        from: new Date(udForm.from).toISOString(),
        to: new Date(udForm.to).toISOString(),
        reason: udForm.reason || null,
      })
      setUdForm({ from: '', to: '', reason: '' })
      setShowUdForm(false)
      fetchSpot()
    } catch (err) {
      console.error('Error adding unavailable date:', err)
      showError(err)
    } finally {
      setUdSubmitting(false)
    }
  }

  const handleDeleteUnavailableDate = (udId) => {
    setConfirmModal({
      open: true,
      title: 'Delete Unavailable Date',
      message: 'Are you sure you want to remove this unavailable date?',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await parkingSpotApi.deleteUnavailableDate(parkingSpotId, udId)
          fetchSpot()
        } catch (err) {
          console.error('Error deleting unavailable date:', err)
          showError(err)
        }
        setConfirmModal(prev => ({ ...prev, open: false }))
      }
    })
  }

  const formatTime = (time) => {
    if (!time) return 'N/A'
    return time.substring(0, 5)
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleCheckOut = async (reservationId) => {
    try {
      await parkingReservationApi.checkOut(reservationId)
      fetchReservations()
    } catch (err) {
      console.error('Error checking out reservation:', err)
      showError(err)
    }
  }

  const getReservationStatus = (reservation) => {
    if (reservation.isCheckedIn) return { label: 'Checked In', variant: 'info' }
    if (reservation.isDeclined) return { label: 'Declined', variant: 'danger' }
    if (reservation.isConfirmed) return { label: 'Confirmed', variant: 'success' }
    return { label: 'Pending', variant: 'warning' }
  }

  if (loading) return <LoadingSpinner size="large" />
  if (error) return <ErrorMessage message={error} />

  return (
    <DetailPageLayout
      title={spot?.name || 'Parking Spot Details'}
      subtitle="View parking spot information and reservations"
      backLink={{ label: 'Back to Parking', href: '/parking' }}
      actions={[
        <Button
          key="edit"
          as={Link}
          to={`/parking/${buildingId || spot?.buildingId}/spots/${parkingSpotId}/edit`}
          variant="secondary"
          icon={<Edit3 className="w-4 h-4" />}
        >
          Edit
        </Button>,
        <Button
          key="delete"
          onClick={handleDeleteSpot}
          variant="danger"
          icon={<Trash2 className="w-4 h-4" />}
        >
          Delete
        </Button>
      ]}
    >
      <div className="space-y-6">
        {/* Spot Info */}
        <Card>
          <CardHeader
            title="Parking Spot Information"
            subtitle="Details about this parking spot"
          />
          <CardBody>
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Name</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {spot?.name || 'N/A'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Spot Number</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {spot?.spotNumber || 'N/A'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Floor</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {spot?.floor || 'N/A'}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Available Hours</div>
                <div className="col-span-2 text-sm text-gray-900 flex items-center">
                  <Clock className="w-4 h-4 mr-1 text-gray-400" />
                  {spot?.isAvailable24_7
                    ? '24/7'
                    : `${formatTime(spot?.startTime)} - ${formatTime(spot?.endTime)}`
                  }
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Features</div>
                <div className="col-span-2 flex flex-wrap gap-2">
                  {spot?.isHandicapAccessible && (
                    <Badge variant="info" size="small">
                      <Accessibility className="w-3 h-3 mr-1" /> Handicap Accessible
                    </Badge>
                  )}
                  {spot?.isElectricCharging && (
                    <Badge variant="success" size="small">
                      <Zap className="w-3 h-3 mr-1" /> EV Charging
                    </Badge>
                  )}
                  {spot?.isAvailable24_7 && (
                    <Badge variant="secondary" size="small">
                      <Clock3 className="w-3 h-3 mr-1" /> 24/7
                    </Badge>
                  )}
                  {!spot?.isHandicapAccessible && !spot?.isElectricCharging && !spot?.isAvailable24_7 && (
                    <span className="text-sm text-gray-500">No special features</span>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Roles</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {spot?.rolesDescription
                    ? <div dangerouslySetInnerHTML={{ __html: spot.rolesDescription }} />
                    : 'All roles'
                  }
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-sm font-medium text-gray-500">Total Reservations</div>
                <div className="col-span-2 text-sm text-gray-900">
                  {spot?.totalReservations || 0}
                </div>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Unavailable Dates */}
        <Card>
          <CardHeader
            title="Unavailable Dates"
            subtitle="Dates when this parking spot is not available"
            action={
              <Button
                onClick={() => setShowUdForm(!showUdForm)}
                variant="secondary"
                size="small"
                icon={<Plus className="w-3 h-3" />}
              >
                Add Date
              </Button>
            }
          />
          <CardBody>
            {showUdForm && (
              <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    label="From"
                    type="datetime-local"
                    value={udForm.from}
                    onChange={(e) => setUdForm(prev => ({ ...prev, from: e.target.value }))}
                    required
                  />
                  <FormField
                    label="To"
                    type="datetime-local"
                    value={udForm.to}
                    onChange={(e) => setUdForm(prev => ({ ...prev, to: e.target.value }))}
                    required
                  />
                </div>
                <FormTextarea
                  label="Reason (optional)"
                  value={udForm.reason}
                  onChange={(e) => setUdForm(prev => ({ ...prev, reason: e.target.value }))}
                  rows={2}
                  placeholder="e.g., Maintenance, Renovation..."
                />
                <div className="flex gap-2">
                  <Button
                    onClick={handleAddUnavailableDate}
                    variant="primary"
                    size="small"
                    loading={udSubmitting}
                    disabled={!udForm.from || !udForm.to}
                  >
                    Save
                  </Button>
                  <Button
                    onClick={() => { setShowUdForm(false); setUdForm({ from: '', to: '', reason: '' }) }}
                    variant="ghost"
                    size="small"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            {(!spot?.unavailableDates || spot.unavailableDates.length === 0) ? (
              <EmptyState
                icon={CalendarOff}
                title="No unavailable dates"
                description="This parking spot has no scheduled unavailable dates."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>From</TableCell>
                    <TableCell header>To</TableCell>
                    <TableCell header>Reason</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {spot.unavailableDates.map((ud) => (
                    <TableRow key={ud.parkingUnavailableDateId}>
                      <TableCell>
                        <div className="text-sm">{formatDateTime(ud.from)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">{formatDateTime(ud.to)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm text-gray-600">{ud.reason || '-'}</div>
                      </TableCell>
                      <TableCell>
                        <Button
                          onClick={() => handleDeleteUnavailableDate(ud.parkingUnavailableDateId)}
                          variant="ghost"
                          size="small"
                          icon={<Trash2 className="w-3 h-3" />}
                        >
                          Delete
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>

        {/* Reservations */}
        <Card>
          <CardHeader
            title="Reservations"
            subtitle="Parking reservations for this spot"
          />
          <CardBody>
            {reservationsLoading ? (
              <LoadingSpinner />
            ) : reservations.length === 0 ? (
              <EmptyState
                icon={Calendar}
                title="No reservations"
                description="No reservations found for this parking spot."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableCell header>Member</TableCell>
                    <TableCell header>Reservation Date</TableCell>
                    <TableCell header>End Date</TableCell>
                    <TableCell header>Status</TableCell>
                    <TableCell header>Message</TableCell>
                    <TableCell header>Actions</TableCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reservations.map((reservation) => {
                    const status = getReservationStatus(reservation)
                    return (
                      <TableRow key={reservation.parkingReservationId}>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <User className="w-4 h-4 mr-1 text-gray-400" />
                            {reservation.memberName || `Member #${reservation.buildingMemberId}`}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center text-sm">
                            <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                            {formatDate(reservation.reservationDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600">
                            {formatDate(reservation.reservationEndDate)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={status.variant} size="small">
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm text-gray-600 max-w-xs truncate">
                            {reservation.message || '-'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {reservation.isCheckedIn && (
                              <Button
                                onClick={() => handleCheckOut(reservation.parkingReservationId)}
                                variant="secondary"
                                size="small"
                                icon={<LogOut className="w-3 h-3" />}
                              >
                                Check Out
                              </Button>
                            )}
                            {!reservation.isConfirmed && !reservation.isDeclined && !reservation.isCheckedIn && (
                              <>
                                <Button
                                  onClick={() => handleConfirm(reservation.parkingReservationId)}
                                  variant="accent"
                                  size="small"
                                  icon={<CheckCircle className="w-3 h-3" />}
                                >
                                  Confirm
                                </Button>
                                <Button
                                  onClick={() => handleDecline(reservation.parkingReservationId)}
                                  variant="danger"
                                  size="small"
                                  icon={<XCircle className="w-3 h-3" />}
                                >
                                  Decline
                                </Button>
                              </>
                            )}
                            <Button
                              onClick={() => handleDeleteReservation(reservation.parkingReservationId)}
                              variant="ghost"
                              size="small"
                            >
                              Delete
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmationModal
        isOpen={confirmModal.open}
        onClose={() => setConfirmModal(prev => ({ ...prev, open: false }))}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmVariant={confirmModal.variant}
        confirmText="Delete"
      />
    </DetailPageLayout>
  )
}

export default ParkingSpotDetail
